'use client';

import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { fmtRange, listNames } from '@/lib/format';

/**
 * THE MOMENT IT BECOMES YOURS
 * ===========================
 *
 * A registration used to end the way a form ends: the sheet shut and a card
 * appeared saying you were in. True, and completely flat — the same card you
 * would see a week later, arriving at the one moment somebody actually feels
 * something about it.
 *
 * So the finishing is its own moment and the card is only the record of it.
 * This takes the whole screen in the event's own colour, says the one thing
 * worth saying, and then leaves by BECOMING that card: the panel folds from
 * full-bleed down to the size of the poster and files itself into the
 * registration card on the page behind. The page is revealed around the card
 * as it shrinks rather than behind a fade, because the panel is the object in
 * motion and not a veil over one.
 *
 * Lifted in spirit from the women's conference welcome curtain, and generic
 * where that one is specific: nothing here knows about Flannel Fest. The
 * colour, the artwork, the title, the date and the names all arrive as props,
 * so any event gets the same moment in its own clothes.
 *
 * THE POSTER NEVER MOVES. It is on screen from the first frame, as the thing
 * the words sit under — not a wash behind them and not something that fades in
 * at the end. When the panel folds it folds ONTO the poster: the panel travels
 * from the whole viewport down to the poster's own rectangle while the poster
 * travels, in panel coordinates, from that rectangle out to the panel's edges.
 * Same duration, same easing, so the two cancel and the artwork sits perfectly
 * still while the colour closes in around it. What flies away afterwards is
 * the picture somebody was already looking at, which is the whole point —
 * before, the art appeared from nowhere at the moment of folding and read as a
 * second thing.
 *
 * ONCE PER REGISTRATION, NOT PER DEVICE. The conference greets somebody once
 * and remembers forever, because arriving at a site is not an achievement.
 * Finishing a registration is, and somebody who signs up their second child a
 * week later has done it again.
 *
 * Details that are load-bearing rather than decorative, most of them learned
 * the hard way on the conference:
 *
 * - **The root is transparent; the panel carries the colour.** That is what
 *   lets the real page appear around the card mid-fold. An opaque root would
 *   hide the page until the very end and leave a dead field while it folded.
 * - **The fold animates inset, not `scale`.** A viewport is not the poster's
 *   aspect ratio, so scaling would squash the art. It costs layout on one
 *   element for half a second, and it is worth it.
 * - **It never starts leaving in a hidden tab.** A backgrounded tab freezes
 *   the animation timeline at 0, so anything begun there never advances and
 *   never resolves — the card would hang mid-page forever.
 * - **Teardown is on timers, not on `finished`.** That promise never settles
 *   in a backgrounded tab; timers still fire.
 * - **It is dismissible** by click, tap, or any key. Nobody is held here.
 * - **Reduced motion gets no fold and no flight**, just a fade.
 */

const HOLD_MS = 2300;
/** The panel contracting from the whole viewport down to one card. */
const FOLD_MS = 560;
const FLIGHT_MS = 700;
/** Poster art is usually landscape; used only until the real image measures. */
const FALLBACK_ASPECT = 3 / 2;

export interface RegisteredCurtainProps {
  /** Who just registered, first names. Drives "You're in" versus "You're all in". */
  names: string[];
  title: string;
  start: string | null;
  end: string | null;
  /** Where the panel folds down to, and the face of the card that flies. */
  image: string | null;
  /** The venue line, when there is one worth repeating. */
  where: string | null;
  /**
   * The card on the page this files itself into. A function rather than an
   * element because the card is inside the widget's shadow root and this
   * renders in the overlay host's — there is no id lookup that crosses both.
   */
  target: () => HTMLElement | null;
  onDone: () => void;
}

export function RegisteredCurtain({
  names,
  title,
  start,
  end,
  image,
  where,
  target,
  onDone,
}: RegisteredCurtainProps) {
  const [visible, setVisible] = useState(true);
  const root = useRef<HTMLDivElement>(null);
  /** The coloured panel. This is the thing that folds and then travels. */
  const panel = useRef<HTMLDivElement>(null);
  /** The words. They go before the panel becomes a card. */
  const column = useRef<HTMLDivElement>(null);
  /** The poster, which is the card's face once folded. */
  const art = useRef<HTMLDivElement>(null);
  const poster = useRef<HTMLImageElement>(null);
  const leaving = useRef(false);

  useEffect(() => {
    const html = document.documentElement;
    const wasOverflow = html.style.overflow;
    html.style.overflow = 'hidden';

    let stopped = false;
    let timer = 0;

    const start = () => {
      if (stopped || document.hidden) return;
      timer = window.setTimeout(leave, HOLD_MS);
    };
    // A tab opened in the background gets its moment when it is looked at,
    // not while nobody is watching.
    const onVisibility = () => {
      if (!document.hidden && !timer && !leaving.current) start();
    };
    const onKey = () => leave();

    start();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('keydown', onKey);

    return () => {
      stopped = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('keydown', onKey);
      html.style.overflow = wasOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function finish() {
    // Hide the real node before asking React to drop it. `setVisible(false)`
    // goes through the concurrent scheduler, which a backgrounded tab defers:
    // the teardown timer fires, this runs, and the unmount can still be queued
    // — leaving the card parked on screen.
    if (root.current) root.current.style.display = 'none';
    document.documentElement.style.overflow = '';
    setVisible(false);
    onDone();
  }

  function leave() {
    if (leaving.current) return;
    leaving.current = true;

    const panelEl = panel.current;
    const to = target();
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const fade = (el: HTMLElement | null, ms: number, delay = 0) =>
      el?.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: ms,
        delay,
        easing: 'ease-out',
        fill: 'forwards',
      });

    // Nothing to fly into, or somebody who asked not to be moved: fade out.
    if (reduced || !panelEl || !to) {
      fade(panelEl, 320);
      window.setTimeout(finish, 380);
      return;
    }

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    /**
     * Where the poster already is. The fold goes to exactly this, rather than
     * to a rectangle worked out from the artwork's aspect and dropped in the
     * middle — those are nearly the same place and "nearly" is what makes a
     * picture jump.
     */
    const artEl = art.current;
    const r = artEl?.getBoundingClientRect();
    const cardL = r ? r.left : (vw - Math.min(420, vw * 0.7)) / 2;
    const cardT = r ? r.top : (vh - Math.min(420, vw * 0.7) / FALLBACK_ASPECT) / 2;
    const cardW = r ? r.width : Math.min(420, vw * 0.7);
    const cardH = r ? r.height : cardW / FALLBACK_ASPECT;

    // 1. The fold. The words leave; the poster stays exactly where it is while
    //    the panel closes onto it — see the note at the top of the file.
    fade(column.current, 220);
    if (artEl) {
      // Out of flow first, at the exact place it already occupies, so taking
      // it out of the column moves nothing.
      artEl.style.position = 'absolute';
      artEl.style.top = `${cardT}px`;
      artEl.style.left = `${cardL}px`;
      artEl.style.width = `${cardW}px`;
      artEl.style.height = `${cardH}px`;
      artEl.style.margin = '0';
      artEl.animate(
        [
          { top: `${cardT}px`, left: `${cardL}px`, width: `${cardW}px`, height: `${cardH}px` },
          { top: '0px', left: '0px', width: `${cardW}px`, height: `${cardH}px` },
        ],
        { duration: FOLD_MS, easing: 'cubic-bezier(0.5, 0.02, 0.24, 1)', fill: 'forwards' }
      );
    }

    panelEl.animate(
      [
        {
          top: '0px',
          right: '0px',
          bottom: '0px',
          left: '0px',
          borderRadius: '0px',
          boxShadow: '0 0 0 rgb(0 0 0 / 0)',
        } as Keyframe,
        {
          top: `${cardT}px`,
          right: `${vw - cardL - cardW}px`,
          bottom: `${vh - cardT - cardH}px`,
          left: `${cardL}px`,
          borderRadius: '18px',
          boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.45)',
        } as Keyframe,
      ],
      { duration: FOLD_MS, easing: 'cubic-bezier(0.5, 0.02, 0.24, 1)', fill: 'forwards' }
    );

    // 2. The flight, once there is a card to fly. Transform only, so it never
    //    fights the inset the fold left behind.
    const liftOff = window.setTimeout(() => {
      const box = to.getBoundingClientRect();
      const dx = box.left + box.width / 2 - (cardL + cardW / 2);
      const dy = box.top + box.height / 2 - (cardT + cardH / 2);
      // Toward the card's width, not to nothing: it is being filed, not
      // deleted, and at scale 0 that reads as a vanish.
      const end = Math.max(box.width / cardW, 0.1);

      panelEl.animate(
        [
          { transform: 'translate(0, 0) scale(1) rotate(0deg)', opacity: 1 },
          {
            // Across before down, with a little turn, so it travels like a
            // card being put away rather than sliding on rails.
            transform: `translate(${dx * 0.58}px, ${dy * 0.3}px) scale(${(1 + end) / 2}) rotate(-5deg)`,
            opacity: 1,
            offset: 0.55,
          },
          {
            transform: `translate(${dx}px, ${dy}px) scale(${end}) rotate(-10deg)`,
            opacity: 0,
          },
        ],
        { duration: FLIGHT_MS, easing: 'cubic-bezier(0.42, 0, 0.62, 0.18)', fill: 'forwards' }
      );
    }, FOLD_MS);

    const teardown = window.setTimeout(() => {
      window.clearTimeout(liftOff);
      finish();
    }, FOLD_MS + FLIGHT_MS);

    // Belt and braces: if this component is torn down mid-exit, the timers go
    // with it rather than firing against a node that is no longer there.
    void teardown;
  }

  if (!visible) return null;

  const when = fmtRange(start, end);
  const who = listNames(names);

  return (
    <div
      ref={root}
      className="fixed inset-0 z-[90]"
      onClick={leave}
      role="status"
      aria-live="polite"
    >
      <div
        ref={panel}
        className="ed bg-teal absolute overflow-hidden"
        style={{ top: 0, right: 0, bottom: 0, left: 0 }}
      >
        {/* The artwork, blown up and blurred: the same treatment the page's
            own hero wears, so this reads as the event and not as a dialog. */}
        {image && (
          <>
            <img
              src={image}
              alt=""
              className="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover opacity-20 blur-3xl"
            />
            <div className="absolute inset-0 bg-black/10" />
          </>
        )}

        <div className="on-art relative flex h-full flex-col items-center justify-center px-8 text-center">
          {/* The poster, on screen from the first frame and the thing the
              words sit under. It does not move again: the fold closes the
              panel onto this exact rectangle. */}
          {image && (
            <div ref={art} className="w-[min(20rem,70vw)] overflow-hidden rounded-2xl shadow-2xl">
              <img ref={poster} src={image} alt="" className="block h-full w-full object-cover" />
            </div>
          )}

          <div ref={column} className="flex flex-col items-center">
            {!image && (
              <span className="mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-white/15">
                <Check className="h-7 w-7" strokeWidth={2.5} />
              </span>
            )}
            <p
              className={`text-3xl font-extrabold tracking-tight sm:text-4xl ${image ? 'mt-7' : ''}`}
            >
              {names.length > 1 ? "You're all in." : "You're in."}
            </p>
            {who && <p className="mt-2 text-base font-semibold opacity-90">{who}</p>}
            <p className="mt-5 text-sm opacity-75">
              {title}
              {when ? ` · ${when}` : ''}
            </p>
            {where && <p className="mt-0.5 text-sm opacity-75">{where}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
